import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Award, Users, TrendingUp, Clock, Star } from 'lucide-react';
import { quizService } from '../lib/supabase';
import CanetaPersonagem from '../components/CanetaPersonagem';
import toast, { Toaster } from 'react-hot-toast';

const Ranking = () => {
  const [ranking, setRanking] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    guardioes: 0,
    historiadores: 0,
    exploradores: 0,
    calouros: 0
  });
  const [filtro, setFiltro] = useState('todos');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarRanking();
  }, []);

  // Função auxiliar para calcular percentual baseado na pontuação máxima
  const calcularPercentual = (score) => {
    const maxScore = ranking.length > 0 ? Math.max(...ranking.map(p => p.score)) : 100;
    const totalPontos = maxScore > 0 ? maxScore : 100;
    return (score / totalPontos) * 100;
  };

  const carregarRanking = async () => {
    try {
      setLoading(true);
      const { data, error } = await quizService.getRanking();
      if (error) throw new Error(error);
      
      const rankingData = data || [];
      setRanking(rankingData);
      
      // Calcular estatísticas baseadas na pontuação real
      const estatisticas = {
        total: rankingData.length,
        guardioes: rankingData.filter(p => {
          const maxScore = rankingData.length > 0 ? Math.max(...rankingData.map(player => player.score)) : 100;
          const totalPontos = maxScore > 0 ? maxScore : 100;
          const perc = (p.score / totalPontos) * 100;
          return perc >= 90;
        }).length,
        historiadores: rankingData.filter(p => {
          const maxScore = rankingData.length > 0 ? Math.max(...rankingData.map(player => player.score)) : 100;
          const totalPontos = maxScore > 0 ? maxScore : 100;
          const perc = (p.score / totalPontos) * 100;
          return perc >= 70 && perc < 90;
        }).length,
        exploradores: rankingData.filter(p => {
          const maxScore = rankingData.length > 0 ? Math.max(...rankingData.map(player => player.score)) : 100;
          const totalPontos = maxScore > 0 ? maxScore : 100;
          const perc = (p.score / totalPontos) * 100;
          return perc >= 50 && perc < 70;
        }).length,
        calouros: rankingData.filter(p => {
          const maxScore = rankingData.length > 0 ? Math.max(...rankingData.map(player => player.score)) : 100;
          const totalPontos = maxScore > 0 ? maxScore : 100;
          const perc = (p.score / totalPontos) * 100;
          return perc < 50;
        }).length
      };
      
      setStats(estatisticas);
    } catch (error) {
      toast.error('Erro ao carregar ranking');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtros = [
    { value: 'todos', label: 'Todos', icon: Users },
    { value: 'guardioes', label: 'Guardiões', icon: Trophy },
    { value: 'historiadores', label: 'Historiadores', icon: Medal },
    { value: 'exploradores', label: 'Exploradores', icon: Award },
    { value: 'calouros', label: 'Calouros', icon: Star }
  ];

  const rankingFiltrado = ranking.filter(player => {
    const percentual = calcularPercentual(player.score);
    
    switch (filtro) {
      case 'guardioes':
        return percentual >= 90;
      case 'historiadores':
        return percentual >= 70 && percentual < 90;
      case 'exploradores':
        return percentual >= 50 && percentual < 70;
      case 'calouros':
        return percentual < 50;
      default:
        return true;
    }
  });

  const getIconePosicao = (posicao) => {
    switch (posicao) {
      case 1:
        return <Trophy className="w-6 h-6 text-golden-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-gray-500">#{posicao}</span>;
    }
  };

  const getCorTitulo = (score) => {
    const percentual = calcularPercentual(score);
    
    if (percentual >= 90) return 'text-golden-600 bg-golden-50';
    if (percentual >= 70) return 'text-blue-600 bg-blue-50';
    if (percentual >= 50) return 'text-green-600 bg-green-50';
    return 'text-purple-600 bg-purple-50';
  };

  const getTitulo = (score) => {
    const percentual = calcularPercentual(score);
    
    if (percentual >= 90) return 'Guardião do Conhecimento';
    if (percentual >= 70) return 'Historiador Acadêmico';
    if (percentual >= 50) return 'Explorador do Saber';
    return 'Calouro Curioso';
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatarTempo = (segundos) => {
    if (!segundos || segundos === 0) return '--';
    const minutos = Math.floor(segundos / 60);
    const segsRestantes = segundos % 60;
    return `${minutos}:${segsRestantes < 10 ? '0' : ''}${segsRestantes}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando ranking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-gray-50 w-full">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-6">
            <CanetaPersonagem estado="celebrando" tamanho="lg" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-amazonia-dark-800 mb-4">
            🏆 Ranking da Caneta Dourada
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Confira os melhores desempenhos na jornada pela Caneta Dourada! 
            Veja quem conseguiu os melhores resultados no quiz sobre a história do ICSA.
          </p>
        </motion.div>

        {/* Estatísticas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Users className="w-8 h-8 text-amazonia-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-amazonia-green-600">{stats.total}</div>
            <div className="text-sm text-gray-600">Participantes</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Trophy className="w-8 h-8 text-golden-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-golden-600">{stats.guardioes}</div>
            <div className="text-sm text-gray-600">Guardiões</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Medal className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-600">{stats.historiadores}</div>
            <div className="text-sm text-gray-600">Historiadores</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-600">{stats.exploradores}</div>
            <div className="text-sm text-gray-600">Exploradores</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-purple-600">{stats.mediapontuacao}</div>
            <div className="text-sm text-gray-600">Média</div>
          </div>
        </motion.div>

        {/* Filtros */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-amazonia-dark-800 mb-4">
              Filtrar por categoria:
            </h3>
            <div className="flex flex-wrap gap-3">
              {filtros.map((filtroItem) => {
                const IconComponent = filtroItem.icon;
                return (
                  <button
                    key={filtroItem.value}
                    onClick={() => setFiltro(filtroItem.value)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filtro === filtroItem.value
                        ? 'bg-amazonia-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-amazonia-green-50 hover:text-amazonia-green-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{filtroItem.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Pódio (Top 3) */}
        {filtro === 'todos' && rankingFiltrado.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <div className="bg-gradient-to-r from-amazonia-green-600 to-golden-600 rounded-xl p-8 text-white">
              <h2 className="text-2xl font-bold text-center mb-8">🏆 Pódio dos Campeões</h2>
              
              <div className="flex items-end justify-center space-x-4">
                {/* 2º Lugar */}
                {rankingFiltrado[1] && (
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-center"
                  >
                    <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-4 h-32 flex flex-col justify-end">
                      <Medal className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <div className="font-bold text-lg">{rankingFiltrado[1].name}</div>
                      <div className="text-sm opacity-90">{rankingFiltrado[1].score} pontos</div>
                    </div>
                    <div className="bg-gray-300 h-16 rounded-t-lg flex items-center justify-center font-bold text-gray-700">
                      2º
                    </div>
                  </motion.div>
                )}

                {/* 1º Lugar */}
                {rankingFiltrado[0] && (
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="text-center"
                  >
                    <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-4 h-40 flex flex-col justify-end">
                      <Trophy className="w-10 h-10 text-golden-300 mx-auto mb-2" />
                      <div className="font-bold text-xl">{rankingFiltrado[0].name}</div>
                      <div className="text-sm opacity-90">{rankingFiltrado[0].score} pontos</div>
                      <div className="text-xs opacity-75 mt-1 flex items-center justify-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatarTempo(rankingFiltrado[0].time_spent_seconds)}</span>
                      </div>
                    </div>
                    <div className="bg-golden-400 h-24 rounded-t-lg flex items-center justify-center font-bold text-golden-900">
                      1º
                    </div>
                  </motion.div>
                )}

                {/* 3º Lugar */}
                {rankingFiltrado[2] && (
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="text-center"
                  >
                    <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-4 h-28 flex flex-col justify-end">
                      <Award className="w-8 h-8 text-amber-300 mx-auto mb-2" />
                      <div className="font-bold text-lg">{rankingFiltrado[2].name}</div>
                      <div className="text-sm opacity-90">{rankingFiltrado[2].score} pontos</div>
                    </div>
                    <div className="bg-amber-600 h-12 rounded-t-lg flex items-center justify-center font-bold text-amber-900">
                      3º
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Lista Completa do Ranking */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="bg-amazonia-green-600 text-white px-6 py-4">
            <h2 className="text-xl font-bold">
              Ranking Completo ({rankingFiltrado.length} participantes)
            </h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            {rankingFiltrado.map((player, index) => (
              <motion.div
                key={player.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {getIconePosicao(index + 1)}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-amazonia-dark-800">
                        {player.name}
                      </h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCorTitulo(player.score)}`}>
                          {getTitulo(player.score)}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatarTempo(player.time_spent_seconds)}</span>
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatarData(player.completed_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-amazonia-green-600">
                      {player.score}
                    </div>
                    <div className="text-sm text-gray-500">
                      pontos
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mensagem quando não há resultados */}
        {rankingFiltrado.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <CanetaPersonagem estado="triste" tamanho="lg" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2 mt-4">
              Nenhum participante encontrado
            </h3>
            <p className="text-gray-500">
              Tente selecionar um filtro diferente ou aguarde mais participações.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Ranking;
